import * as React from 'react'
import { ReactElement, useMemo, useRef, useState } from 'react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogOpenChangeData,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    makeStyles,
    ToolbarButton,
} from '@fluentui/react-components'
import { tokens } from '@fluentui/react-theme'
import {
    bundleIcon,
    DismissFilled,
    DismissRegular,
    MailArrowForwardFilled,
    MailArrowForwardRegular,
    MailCopyFilled,
    MailCopyRegular,
} from '@fluentui/react-icons'
import { EmailToolbarProps } from '../../Toolbar/Email/EmailToolbar'
import ProcessEmailForm, { EmailProcessingAction, EmailProcessingActionLabelMap } from './ProcessEmailForm'
import { useTranslation } from 'react-i18next'

export type ProcessEmailDialogProps = Pick<
    EmailToolbarProps,
    'selection' | 'isLoading' | 'setPreventDefaultEvent' | 'file' | 'reset'
> & {
    action: EmailProcessingAction
    userEmail: string // The e-mail address in which the e-mail resides. Can be the users or the replications.
}

export const EmailProcessingActionIconMap = new Map([
    [EmailProcessingAction.Copy, bundleIcon(MailCopyFilled, MailCopyRegular)],
    [EmailProcessingAction.Move, bundleIcon(MailArrowForwardFilled, MailArrowForwardRegular)],
    [EmailProcessingAction.File, bundleIcon(MailArrowForwardFilled, MailArrowForwardRegular)],
    [EmailProcessingAction.Ignore, bundleIcon(DismissFilled, DismissRegular)],
])

const useStyles = makeStyles({
    primaryButton: {
        backgroundColor: tokens.colorBrandForeground1,
        '&:hover, &:active': {
            backgroundColor: tokens.colorBrandForeground2,
        },
        '&:hover:active': {
            backgroundColor: tokens.colorBrandForeground2,
        },
    },
})

export default function ProcessEmailDialog(props: ProcessEmailDialogProps): ReactElement {
    const [open, setOpen] = useState(false)
    const submitForm = useRef<() => Promise<void>>()
    const [isSubmitting, setIsSubmitting] = useState<boolean>()

    const IconComponent = EmailProcessingActionIconMap.get(props.action)
    const styles = useStyles()
    const { t: translate } = useTranslation()

    const dialogSubject = useMemo(() => {
        return translate('email.filing.dialog.subject', {
            count: props.selection.getSelectedCount(),
            action: translate(EmailProcessingActionLabelMap.get(props.action)),
        })
    }, [props.selection.getSelectedCount(), props.action])

    const toggleDialog = (open: boolean) => {
        setOpen(open)
        props.setPreventDefaultEvent(open)
    }

    return (
        <Dialog open={open} onOpenChange={(_, data: DialogOpenChangeData) => toggleDialog(data.open)}>
            <DialogTrigger disableButtonEnhancement>
                <ToolbarButton
                    icon={<IconComponent />}
                    disabled={props.isLoading}
                    hidden={props.selection.getSelectedCount() === 0}
                    onClick={() => toggleDialog(true)}
                >
                    {translate(EmailProcessingActionLabelMap.get(props.action))}
                </ToolbarButton>
            </DialogTrigger>
            <DialogSurface>
                <DialogBody>
                    <DialogTitle>{dialogSubject}</DialogTitle>
                    <DialogContent>
                        <ProcessEmailForm
                            {...props}
                            onDismiss={() => toggleDialog(false)}
                            setSubmitForm={submit => (submitForm.current = submit)}
                            setIsSubmitting={submitting => setIsSubmitting(submitting)}
                        />
                    </DialogContent>
                    <DialogActions>
                        <DialogTrigger disableButtonEnhancement>
                            <Button
                                appearance="transparent"
                                icon={<DismissRegular />}
                                onClick={() => toggleDialog(false)}
                            >
                                {translate('action.cancel')}
                            </Button>
                        </DialogTrigger>
                        <Button
                            appearance="primary"
                            disabled={isSubmitting}
                            icon={<IconComponent />}
                            className={styles.primaryButton}
                            onClick={() => submitForm.current()}
                        >
                            {translate(EmailProcessingActionLabelMap.get(props.action))}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    )
}
